/** Textos dos átomos de elegibilidade: "o que falta" e tag "Pré-alocado" (WEB2). */
import { DEFAULT_UTC_OFFSET, formatDateInOffset, formatHourMinuteInOffset } from '@/utils/date'
import {
  expiresBeforeStation,
  expiresBeforeStationText
} from '@/components/ProfessionalDocuments/labels'
import {
  ProfessionalDocumentPendingReason,
  ProfessionalDocumentStatus
} from '@/types/professional-documents.types'
import { PreAllocationStatus, QualificationOnFail } from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationTag } from '@/types/qualification-eligibility.types'

export const ON_FAIL_LABEL: Record<QualificationOnFail, string> = {
  [QualificationOnFail.BLOCK]: 'Bloqueia',
  [QualificationOnFail.PRE_ALLOCATE]: 'Permite pré-alocação'
}

type MissingStatusInput = Pick<EligibilityReason, 'status' | 'pendingReason' | 'expiresAt'>

/** Status do item que falta (motivo de selo vem com `status: null` → Pendente). */
export function missingStatusText(reason: MissingStatusInput): string {
  if (reason.status === ProfessionalDocumentStatus.IN_REVIEW) return 'Em Análise'
  if (reason.status === ProfessionalDocumentStatus.CONCLUDED) return 'Concluído'
  if (reason.pendingReason === ProfessionalDocumentPendingReason.REJECTED) return 'Reprovado'
  // D96: ainda vale hoje, mas vence antes do plantão (D91) — não é "vencido".
  if (expiresBeforeStation(reason)) return expiresBeforeStationText(reason.expiresAt)
  if (reason.pendingReason === ProfessionalDocumentPendingReason.EXPIRED) return 'Vencido'
  return 'Pendente'
}

const withStatus = (name: string, reason: MissingStatusInput) =>
  `${name} (${missingStatusText(reason).toLowerCase()})`

/** "Certificado ACLS (em análise), ATLS (reprovado)". */
export function summarizeMissing(
  missing: EligibilityReason[],
  nameOf: (reason: EligibilityReason) => string
): string {
  return missing.map((reason) => withStatus(nameOf(reason), reason)).join(', ')
}

/** Pré-alocação OPEN do plantão, ou `null` (resolvida/cancelada não vira tag). */
export function openPreAllocation(
  source: { preAllocation?: PreAllocationTag | null } | null | undefined
): PreAllocationTag | null {
  const tag = source?.preAllocation
  return tag?.status === PreAllocationStatus.OPEN ? tag : null
}

/**
 * "dd/mm/aaaa HH:mm" na hora do plantão: o ISO traz o offset do plantão (R2.3), lido sem
 * converter para o fuso do navegador nem para São Paulo. Mesmo regex do `formatStationStart`
 * (R26); sem offset legível (`Z`) lê em UTC.
 */
function formatDeadline(iso: string): string {
  const offset =
    /[+-]\d{2}:?\d{2}$/.exec(iso)?.[0] ?? (iso.endsWith('Z') ? '+00:00' : DEFAULT_UTC_OFFSET)
  const date = formatDateInOffset(iso, offset)
  const time = formatHourMinuteInOffset(iso, offset)
  return date && time ? `${date} ${time}` : '-'
}

/** Título (tooltip) da tag: o que falta, com status, e o prazo = início do plantão. */
export function preAllocationTitle(tag: PreAllocationTag): string {
  const pending = tag.missing.map((item) => withStatus(item.name, item)).join(', ')
  const deadline = `até ${formatDeadline(tag.deadline)}`
  return pending ? `Pré-alocado · pendente: ${pending} · ${deadline}` : `Pré-alocado · ${deadline}`
}
